import { useState } from 'react';
import { saveServerSetting, type ServerSetting } from '../api/servers';
import { ca } from '../i18n/ca';
import { eraLabel, useEras } from './useEras';

/**
 * A server's setting on its own page: the eras a night on it can be set in and
 * the one the quick start proposes (docs/design/SERVER_SETTING_STUDY.md).
 *
 * The platform reads a suggestion off what the server runs — a WW2 conversion
 * makes a WW2 server, the Creator DLCs of a modern one add the eras they bring —
 * and whoever manages the servers confirms or corrects it once. The era itself
 * is chosen per mission.
 *
 * The card says what stands, as chips, and offers one button. A proposal is
 * ordinary news, not a fault: it is said in the page's own quiet tone, with
 * what it was read from, and "Confirma" keeps it. Changing the eras is behind
 * a fold, since it is done once in a server's life.
 */
export function ServerSettingCard({
  serverId,
  setting,
  canManage,
  onSaved,
}: {
  serverId: number;
  setting: ServerSetting;
  canManage: boolean;
  onSaved: (saved: ServerSetting) => void;
}) {
  const t = ca.serversPage.setting;
  const [eras, setEras] = useState<string[]>(setting.eras);
  const [first, setFirst] = useState(setting.defaultEra);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eraList = useEras();
  const eraName = (era: string) => eraLabel(eraList, era);
  const changed =
    first !== setting.defaultEra ||
    eras.length !== setting.eras.length ||
    eras.some((era) => !setting.eras.includes(era));

  function toggle(era: string) {
    setEras((current) => {
      const next = current.includes(era) ? current.filter((e) => e !== era) : [...current, era];
      // The proposed era is always one of the offered ones.
      if (!next.includes(first) && next.length > 0) setFirst(next[0]);
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      onSaved(await saveServerSetting(serverId, { defaultEra: first, eras }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="card form setting-card" aria-label={t.title}>
      <h2>{t.title}</h2>
      <ul className="era-chips plain" aria-label={t.eras}>
        {eras.map((era) => (
          <li key={era} className={`chip ${era === first ? 'first' : ''}`}>
            {eraName(era)}
          </li>
        ))}
      </ul>
      {eras.length > 0 && <p className="muted small">{t.proposedFirst(eraName(first))}</p>}
      {!setting.confirmed && (
        <p className="muted small proposal" role="status">
          <i className="bi bi-info-circle" aria-hidden="true" /> {t.suggested}
          {setting.because.length > 0 ? ` ${t.because(setting.because)}` : ''}
        </p>
      )}
      <details className="fold">
        <summary>{t.adjust}</summary>
        <div className="fold-body">
          <p className="muted small">{t.intro}</p>
          <fieldset disabled={!canManage || busy}>
            <legend className="muted small">{t.eras}</legend>
            {eraList.map(({ id: era }) => (
              <label key={era} className="check">
                <input
                  type="checkbox"
                  name={`era-${era}`}
                  checked={eras.includes(era)}
                  onChange={() => toggle(era)}
                />
                <span>{eraName(era)}</span>
              </label>
            ))}
          </fieldset>
          <label>
            {t.first}
            <select
              name="defaultEra"
              value={first}
              disabled={!canManage || busy}
              onChange={(e) => setFirst(e.target.value)}
            >
              {eras.map((era) => (
                <option key={era} value={era}>
                  {eraName(era)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </details>
      {canManage && (!setting.confirmed || changed) && (
        <div className="row actions">
          <button
            type="button"
            className={setting.confirmed ? undefined : 'primary'}
            disabled={busy || eras.length === 0}
            onClick={() => void save()}
          >
            {setting.confirmed ? t.save : t.confirm}
          </button>
        </div>
      )}
      {error && (
        <p className="error small" role="alert">
          {error}
        </p>
      )}
    </article>
  );
}
